import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card, Row, Col, Switch, Select, Tag, Typography, Spin, Empty, message,
  Pagination, Alert, Space, Button, Tooltip, Segmented,
} from "antd";
import { ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getCameras,
  getDetectionReview,
  getDetectionReviewConfig,
  setDetectionReviewConfig,
  getDetectionReviewImageUrl,
} from "../network/api";

const { Text } = Typography;

const HIT = "#ff4d4f"; // a box the detector fired on (production would redact)
const MISS = "#8c9bab"; // a near-miss below the threshold, drawn faintly

const pct = (x) => (x == null ? "—" : `${(x * 100).toFixed(0)}%`);

// Boxes drawn over the frame, positioned by original-pixel coords. Only used for FIRES.
function BoxOverlay({ boxes, below, w, h }) {
  if (!w || !h) return null;
  const asStyle = (b) => ({
    position: "absolute",
    left: `${(b.x1 / w) * 100}%`,
    top: `${(b.y1 / h) * 100}%`,
    width: `${((b.x2 - b.x1) / w) * 100}%`,
    height: `${((b.y2 - b.y1) / h) * 100}%`,
    boxSizing: "border-box",
  });
  return (
    <>
      {(below || []).map((b, i) => (
        <div key={`m${i}`} style={{ ...asStyle(b), border: `1px dashed ${MISS}` }} />
      ))}
      {(boxes || []).map((b, i) => (
        <div key={`h${i}`} style={{ ...asStyle(b), border: `2px solid ${HIT}`, background: `${HIT}22` }}>
          {b.score != null && (
            <span
              style={{
                position: "absolute", top: 0, left: 0, transform: "translateY(-100%)",
                background: HIT, color: "#fff", fontSize: 10, lineHeight: "14px",
                padding: "0 4px", whiteSpace: "nowrap",
              }}
            >
              {(b.score * 100).toFixed(0)}%
            </span>
          )}
        </div>
      ))}
    </>
  );
}

// One captured sample. A FIRE shows original vs the same frame with boxes (judge the box —
// false positive?). A MISS shows the single plain original (judge it yourself — is there a
// person the model let through?), with the model's best score so you know how close it got.
function SampleCard({ sample }) {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error | expired
  const urlRef = useRef(null);
  const isMiss = sample.detected === false;

  useEffect(() => {
    let alive = true;
    if (!sample.onDisk) {
      setState("expired");
      return;
    }
    getDetectionReviewImageUrl(sample.id)
      .then((u) => {
        if (!alive) {
          URL.revokeObjectURL(u);
          return;
        }
        urlRef.current = u;
        setUrl(u);
        setState("ok");
      })
      .catch((err) => {
        if (alive) setState(err?.response?.status === 410 ? "expired" : "error");
      });
    return () => {
      alive = false;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [sample.id, sample.onDisk]);

  const pane = (withBoxes) => (
    <div style={{ position: "relative", lineHeight: 0, background: "#111", borderRadius: 4, overflow: "hidden" }}>
      {url ? (
        <img src={url} alt={withBoxes ? "detected" : "original"} style={{ width: "100%", display: "block" }} />
      ) : (
        <div style={{ aspectRatio: "4 / 3", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {state === "loading" ? (
            <Spin />
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {state === "expired" ? "original expired" : "could not load"}
            </Text>
          )}
        </div>
      )}
      {withBoxes && url && (
        <BoxOverlay boxes={sample.boxes} below={sample.below} w={sample.imageWidth} h={sample.imageHeight} />
      )}
    </div>
  );

  return (
    <Card
      size="small"
      style={{ height: "100%", borderTop: `3px solid ${isMiss ? MISS : HIT}` }}
      styles={{ body: { padding: 12 } }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <div style={{ minWidth: 0 }}>
          <Text strong ellipsis style={{ display: "block" }}>
            {sample.store || sample.deviceId || "—"}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            #{sample.deviceId || "—"}
            {sample.region ? ` · ${sample.region}` : ""} · {dayjs(sample.createdAt).format("DD MMM HH:mm:ss")}
          </Text>
        </div>
        <Tag color={isMiss ? "default" : "red"} style={{ marginInlineEnd: 0 }}>
          {isMiss ? "no detection" : "detected"}
        </Tag>
      </div>

      {isMiss ? (
        <div>
          {pane(false)}
          <Text type="secondary" style={{ fontSize: 11 }}>
            Model didn't fire — look for a person it missed
          </Text>
        </div>
      ) : (
        <Row gutter={8}>
          <Col span={12}>
            {pane(false)}
            <Text type="secondary" style={{ fontSize: 11 }}>Original</Text>
          </Col>
          <Col span={12}>
            {pane(true)}
            <Text type="secondary" style={{ fontSize: 11 }}>
              Detected — <span style={{ color: HIT }}>■</span> found
              {sample.below?.length ? <> · <span style={{ color: MISS }}>▫</span> near-miss</> : null}
            </Text>
          </Col>
        </Row>
      )}

      <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {isMiss ? (
          <Tag>model's best {pct(sample.maxScore)}</Tag>
        ) : (
          <>
            <Tag>{sample.count} found ≥ {pct(sample.threshold)}</Tag>
            <Tag>max {pct(sample.maxScore)}</Tag>
          </>
        )}
        <Tag color={sample.redactedInProduction ? "red" : "default"}>
          {sample.redactedInProduction ? "blurred live" : "kept live"}
        </Tag>
        {sample.humanModel && <Tag color="blue">{sample.humanModel}</Tag>}
      </div>
    </Card>
  );
}

const RETENTION_OPTS = [
  { value: 3, label: "3 days" },
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
];

const DetectionReview = () => {
  const [config, setConfig] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | true (found) | false (missed)
  const [data, setData] = useState({ data: [], total: 0, counts: { found: 0, missed: 0, all: 0 } });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const limit = 24;

  useEffect(() => {
    getDetectionReviewConfig()
      .then(setConfig)
      .catch(() => setConfig({ enabled: false, retentionDays: 3, includeMisses: true }));
    getCameras(1, 1000, {})
      .then((list) => setCameras(Array.isArray(list) ? list : list?.data || []))
      .catch(() => setCameras([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDetectionReview({
        deviceId: deviceId || undefined,
        detected: filter === "all" ? undefined : filter,
        page,
        limit,
      });
      setData(res || { data: [], total: 0, counts: { found: 0, missed: 0, all: 0 } });
      if (res?.config) setConfig((c) => ({ ...(c || {}), ...res.config }));
    } catch (err) {
      message.error("Could not load review samples");
      setData({ data: [], total: 0, counts: { found: 0, missed: 0, all: 0 } });
    } finally {
      setLoading(false);
    }
  }, [deviceId, filter, page]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = useCallback(async (patch) => {
    setSavingCfg(true);
    try {
      const saved = await setDetectionReviewConfig(patch);
      setConfig((c) => ({ ...(c || {}), ...saved }));
      message.success(
        patch.enabled === true ? "Capture started — it applies to the next frame"
        : patch.enabled === false ? "Capture stopped"
        : patch.includeMisses !== undefined ? (patch.includeMisses ? "Now also keeping misses" : "No longer keeping misses")
        : "Retention updated"
      );
      if (patch.enabled || patch.includeMisses !== undefined) load();
    } catch (err) {
      message.error(err?.response?.data?.error || "Could not update — super admin required");
    } finally {
      setSavingCfg(false);
    }
  }, [load]);

  const enabled = !!config?.enabled;
  const counts = data.counts || { found: 0, missed: 0, all: 0 };

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={[12, 12]} wrap>
          <Col flex="auto" style={{ minWidth: 260 }}>
            <Space align="center">
              <Switch
                checked={enabled}
                loading={savingCfg}
                onChange={(v) => saveConfig({ enabled: v })}
              />
              <span>
                <Text strong>{enabled ? "Capturing frames" : "Capture is off"}</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    While on, frames are kept — the true original next to what the detector did —
                    and auto-deleted after the window.
                  </Text>
                </div>
              </span>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              <Tooltip title="Also keep frames the model did NOT fire on, so you can spot people it missed. These are the more numerous, so a rolling cap keeps disk bounded.">
                <Space size={4}>
                  <Switch
                    size="small"
                    checked={config?.includeMisses !== false}
                    disabled={savingCfg}
                    onChange={(v) => saveConfig({ includeMisses: v })}
                  />
                  <Text style={{ fontSize: 12 }}>Keep misses</Text>
                </Space>
              </Tooltip>
              <Text type="secondary" style={{ fontSize: 12 }}>Keep for</Text>
              <Select
                size="small"
                style={{ width: 100 }}
                value={config?.retentionDays ?? 3}
                options={RETENTION_OPTS}
                disabled={savingCfg}
                onChange={(v) => saveConfig({ retentionDays: v })}
              />
              <Tooltip title="Refresh the gallery">
                <Button size="small" icon={<ReloadOutlined />} onClick={load} />
              </Tooltip>
            </Space>
          </Col>
        </Row>
        <Alert
          type="warning"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginTop: 12 }}
          message="This keeps unredacted person images for the retention window"
          description="It exists to verify the detector against real photos — both its false alarms and the people it misses. Super admins only, auto-deleted on expiry, disk-capped. Turn it off once you've seen enough."
        />
      </Card>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <Space wrap>
          <Segmented
            value={filter}
            onChange={(v) => { setFilter(v); setPage(1); }}
            options={[
              { label: `All (${counts.all})`, value: "all" },
              { label: `Detected (${counts.found})`, value: "true" },
              { label: `Missed? (${counts.missed})`, value: "false" },
            ]}
          />
          <Select
            allowClear showSearch placeholder="All devices" style={{ width: 240 }}
            value={deviceId}
            onChange={(v) => { setDeviceId(v); setPage(1); }}
            optionFilterProp="label"
            options={cameras.map((cam) => ({
              value: cam.deviceId,
              label: cam.shop?.title ? `${cam.shop.title} (#${cam.deviceId})` : (cam.deviceTitle || cam.deviceId),
            }))}
          />
        </Space>
        <Text type="secondary">{data.total} shown · last {config?.retentionDays ?? 3} days</Text>
      </div>

      <Spin spinning={loading}>
        {!loading && !data.data.length ? (
          <Empty
            description={
              !enabled
                ? "Capture is off. Turn it on above to start collecting frames."
                : filter === "false"
                ? "No missed-detection frames captured yet. Make sure “Keep misses” is on."
                : filter === "true"
                ? "No detections captured yet — they appear as the detector fires on new frames."
                : "Nothing captured yet — frames appear here as new photos arrive."
            }
          />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {data.data.map((s) => (
                <Col key={s.id} xs={24} sm={12} lg={8} xxl={6}>
                  <SampleCard sample={s} />
                </Col>
              ))}
            </Row>
            {data.total > limit && (
              <div style={{ textAlign: "center", marginTop: 16 }}>
                <Pagination
                  current={page}
                  pageSize={limit}
                  total={data.total}
                  showSizeChanger={false}
                  onChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </Spin>
    </div>
  );
};

export default DetectionReview;
